import { gsap, Power2 } from 'gsap';
import { Slide } from '../Slide/Slide';
import '../types';

export class Tile extends Slide {
  /**
   * @typedef {options} tileOptions
   *  @property {"top"|"right"|"bottom"|"left"} [type=left] Type of animation
   *  @property {number|string} [amount=5] Amount of tiles to animate or CSS Var
   */

  /**
   * @constructor
   * @class Tile
   * @classdesc Controls all Tile transitions (top, left, bottom, right)
   *            There may be rendering issues depending on the amount of tiles
   * @param {tileOptions} options
   */
  constructor(options) {
    super(options);

    const defaultOptions = {
      type: 'left',
      amount: 5,
    };

    this.options = { ...defaultOptions, ...options, ...this.options };
  }

  /**
   * Gets the extra props needed for dimensions and layout
   * @param {string} type Type of animation
   * @param {number} amount Amount of tiles
   * @returns {object} Props
   */
  _getExtraProps(type) {
    const amount = this._getOptionValue('amount');
    const size = `${(1 / amount) * 100}%`;
    const props = {};

    switch (type) {
      case 'left':
      case 'right':
        props.height = size;
        break;
      case 'top':
      case 'bottom':
        props.display = 'inline-block';
        props.width = size;
        break;
    }

    return props;
  }

  /**
   * Method for the animation "in"
   * @returns {Promise}
   */
  in() {
    return new Promise((resolve) => {
      const { type } = this.options;
      const duration = this._getOptionValue('duration');
      const {
        animateIn: { set, from, to },
      } = this._getAnimationProperties(type);
      const amount = this._getOptionValue('amount');
      const extraProps = this._getExtraProps(type, amount);
      const stagger = 1 / (amount * 2);
      const container = this.createTransitionContainer();
      const tl = new gsap.timeline({
        onComplete: () => {
          document.dispatchEvent(new Event('barbaAnimInEnd'));
          resolve();
        },
      });

      for (let i = 0; i < amount; i++) {
        const rect = document.createElement('div');
        rect.classList.add('wb-transition-tile');
        container.appendChild(rect);
      }

      tl.set('.wb-transition-tile', { ...set, ...extraProps });
      tl.fromTo(
        '.wb-transition-tile',
        { ...from },
        { duration, ease: Power2.easeOut, ...to, stagger }
      );
    });
  }

  /**
   * Method for the animation "out"
   * @returns {Promise}
   */
  out() {
    return new Promise((resolve) => {
      const { type } = this.options;
      const duration = this._getOptionValue('duration');
      const {
        animateOut: { set, to },
      } = this._getAnimationProperties(type);
      const amount = this._getOptionValue('amount');
      const stagger = 1 / (amount * 2);
      const tl = new gsap.timeline({
        onComplete: () => {
          this.getTransitionContainer().remove();
          resolve();
        },
      });

      document.dispatchEvent(new Event('barbaAnimOutStart'));

      tl.set('.wb-transition-tile', { ...set });
      tl.to('.wb-transition-tile', { duration, ease: Power2.easeOut, ...to, stagger });
    });
  }
}
